import { useCallback, useEffect, useState } from 'react';
import { CAlert, CButton, CCard, CCardBody, CCardHeader, CFormCheck, CSpinner, CTable, CTableBody, CTableDataCell, CTableHead, CTableHeaderCell, CTableRow } from '@coreui/react';
import { api, problemMessage, type Schemas } from '../api/client';
import { useMe, useSetMe } from '../auth/AuthGate';

type Member = Schemas['TeamMemberResponse'];
type Role = Schemas['DomainRoleResponse'];

/**
 * Tým — kdo tu je a co dělá. Lidi se načtou z portálu najednou (všichni GlobalAdmini)
 * a role jim nastaví vedení, nemusí se nejdřív sami přihlásit (zpětná vazba 8. 10. 2026).
 * Role pořád nic nepovolují, jen říkají, komu chodí jaká práce (ADR-013).
 */
export function TeamCard() {
  const me = useMe();
  const setMe = useSetMe();
  const [members, setMembers] = useState<Member[] | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);

  const load = useCallback(() => {
    api.GET('/api/v1/team', { params: { query: { includeInactive: true } } })
      .then(({ data, error: problem }) => (data ? setMembers(data) : setError(problemMessage(problem))));
  }, []);

  useEffect(() => {
    load();
    api.GET('/api/v1/team/roles').then(({ data }) => setRoles(data ?? []));
  }, [load]);

  async function importFromPortal() {
    setBusy(true);
    setError(null);
    setInfo(null);
    const { data, error: problem } = await api.POST('/api/v1/team/import');
    setBusy(false);

    if (!data) {
      setError(problemMessage(problem));
      return;
    }

    setInfo(data.created > 0
      ? `Přidáno ${data.created} lidí z portálu. Celkem se sem může přihlásit ${data.total} lidí (GlobalAdmin).`
      : 'Všichni z portálu už v týmu jsou.');
    load();
  }

  async function toggle(member: Member, code: string, checked: boolean) {
    const next = checked ? [...member.roles, code] : member.roles.filter((r) => r !== code);
    setError(null);

    const { data, error: problem } = await api.PUT('/api/v1/team/{memberId}/roles', {
      params: { path: { memberId: member.id } },
      body: { roleCodes: next },
    });

    if (!data) {
      setError(problemMessage(problem));
      return;
    }

    setMembers((all) => all?.map((m) => (m.id === member.id ? { ...m, roles: data } : m)) ?? null);
    // Vlastni role i v profilu - jinak by upozorneni "nemate roli" viselo dal.
    if (member.id === me.memberId) setMe({ ...me, roles: data });
  }

  // Technicke ucty (API, boti) maji v portalu GlobalAdmina taky - odeberou se z tymu.
  async function setActive(member: Member, isActive: boolean) {
    setError(null);
    const { error: problem, response } = await api.PUT('/api/v1/team/{memberId}/active', {
      params: { path: { memberId: member.id } },
      body: { isActive },
    });

    if (response.ok) load(); else setError(problemMessage(problem));
  }

  const active = members?.filter((m) => m.isActive) ?? [];
  const removed = members?.filter((m) => !m.isActive) ?? [];

  return (
    <CCard className="mb-4">
      <CCardHeader className="d-flex justify-content-between align-items-center">
        <strong>Tým</strong>
        <CButton size="sm" color="primary" variant="ghost" disabled={busy} onClick={() => void importFromPortal()}>
          {busy ? 'Načítám…' : 'Načíst lidi z portálu'}
        </CButton>
      </CCardHeader>
      <CCardBody>
        <p className="text-body-secondary">
          Kdo co dělá — podle toho jim chodí práce a dotazy. Lidi se načítají z portálu (jen GlobalAdmini,
          nikdo jiný se sem nepřihlásí). Role můžete nastavit i těm, kdo tu ještě nebyli. Technické
          účty (API, boti) odeberte — odebraný se sem nepřihlásí a import ho nevrátí.
        </p>
        {info && <CAlert color="info">{info}</CAlert>}
        {error && <CAlert color="danger">{error}</CAlert>}
        {!members ? <CSpinner color="primary" /> : (
          <CTable responsive small className="mb-0 align-middle">
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell>Kdo</CTableHeaderCell>
                {roles.map((r) => <CTableHeaderCell key={r.code} className="text-center small">{r.name}</CTableHeaderCell>)}
                <CTableHeaderCell />
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {active.map((m) => (
                <CTableRow key={m.id}>
                  <CTableDataCell>
                    <div className="fw-semibold">{m.displayName}{m.id === me.memberId ? ' (já)' : ''}</div>
                    <div className="small text-body-secondary">{m.email}</div>
                  </CTableDataCell>
                  {roles.map((r) => (
                    <CTableDataCell key={r.code} className="text-center">
                      <CFormCheck aria-label={`${m.displayName} — ${r.name}`} checked={m.roles.includes(r.code)}
                        onChange={(e) => void toggle(m, r.code, e.target.checked)} />
                    </CTableDataCell>
                  ))}
                  <CTableDataCell className="text-end">
                    {m.id !== me.memberId && (
                      <CButton size="sm" color="secondary" variant="ghost" onClick={() => void setActive(m, false)}>Odebrat</CButton>
                    )}
                  </CTableDataCell>
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        )}
        {removed.length > 0 && (
          <div className="mt-3">
            <CButton size="sm" color="link" className="p-0" onClick={() => setShowRemoved(!showRemoved)}>
              {showRemoved ? 'Skrýt odebrané' : `Odebraní (${removed.length})`}
            </CButton>
            {showRemoved && removed.map((m) => (
              <div key={m.id} className="d-flex justify-content-between align-items-center border-bottom py-1 small">
                <span>{m.displayName} <span className="text-body-secondary">· {m.email}</span></span>
                <CButton size="sm" color="primary" variant="ghost" onClick={() => void setActive(m, true)}>Vrátit</CButton>
              </div>
            ))}
          </div>
        )}
      </CCardBody>
    </CCard>
  );
}
